import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Card, Form, Row, Col, Badge, Spinner, ProgressBar } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import {
  CheckCircle,
  AlertCircle,
  FileText,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  ChevronLeft,
  Lightbulb,
  Edit3,
  Eye
} from 'lucide-react';

/**
 * 3-Step Wizard for Creating Verification from Bank Transaction
 * Step 1: Template/Suggestion Selection
 * Step 2: Edit Account Entries
 * Step 3: Review and Confirm
 */
const CreateVerificationFromTransactionModal = ({ show, onHide, transaction, onSuccess }) => {
  const { companyId } = useParams();

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);

  // Data state
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [accountEntries, setAccountEntries] = useState([]);
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [series, setSeries] = useState('A');

  // UI state
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);

  // Detect transaction type (bank vs skatteverket)
  const transactionType = transaction?.statement_id ? 'skatteverket' : 'bank';
  const statementId = transaction?.statement_id || transaction?.id;

  useEffect(() => {
    if (show && transaction) {
      resetWizard();
      fetchFiscalYears();
      fetchSuggestions();
    }
  }, [show, transaction]);

  const resetWizard = () => {
    setCurrentStep(1);
    setSuggestions([]);
    setSelectedSuggestion(null);
    setAccountEntries([]);
    setDescription('');
    setNotes('');
    setSeries('A');
    setError(null);
  };

  const fetchFiscalYears = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/accounting/fiscal-years`),
        { withCredentials: true }
      );

      const years = response.data.fiscalYears || [];
      setFiscalYears(years);

      // Auto-select current fiscal year
      const current = years.find(fy => fy.status === 'active' || fy.is_current);
      if (current) {
        setSelectedFiscalYear(current.fiscal_year_id);
      } else if (years.length > 0) {
        setSelectedFiscalYear(years[0].fiscal_year_id);
      }
    } catch (err) {
      console.error('Error fetching fiscal years:', err);
    }
  };

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);

    try {
      const basePath = transactionType === 'skatteverket'
        ? `${companyId}/accounting/reconciliation/skatteverket/statements/${statementId}/verification-suggestions`
        : `${companyId}/accounting/reconciliation/banking/statements/${statementId}/verification-suggestions`;

      const apiUrl = getApiUrl(basePath);

      const response = await axios.get(apiUrl, { withCredentials: true });

      if (response.data.success) {
        setSuggestions(response.data.suggestions || []);

        // Pre-fill description and notes
        const sourceLabel = transactionType === 'skatteverket' ? 'Skatteverket' : 'Bank';
        setDescription(transaction.description || `${sourceLabel} transaction`);
        setNotes(`Auto-generated from ${sourceLabel.toLowerCase()} statement: ${transaction.description || ''}`);
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError('Kunde inte hämta kontoförslag');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    setSelectedSuggestion(suggestion);

    // Build account entries from suggestion
    const amount = Math.abs(parseFloat(transaction.amount));
    const isIncoming = parseFloat(transaction.amount) > 0;

    const entries = [];

    if (isIncoming) {
      // Incoming: Debit bank, Credit counter-account
      entries.push({
        accountId: suggestion.bankAccount,
        accountName: '1930 - Företagskonto/checkräkningskonto',
        debit: amount,
        credit: 0
      });
      entries.push({
        accountId: suggestion.counterAccount,
        accountName: `${suggestion.counterAccount} - ${suggestion.reason}`,
        debit: 0,
        credit: amount
      });
    } else {
      // Outgoing: Credit bank, Debit counter-account
      entries.push({
        accountId: suggestion.bankAccount,
        accountName: '1930 - Företagskonto/checkräkningskonto',
        debit: 0,
        credit: amount
      });
      entries.push({
        accountId: suggestion.counterAccount,
        accountName: `${suggestion.counterAccount} - ${suggestion.reason}`,
        debit: amount,
        credit: 0
      });
    }

    setAccountEntries(entries);
  };

  const handleSkipSuggestions = () => {
    // Create default entries without suggestion
    const amount = Math.abs(parseFloat(transaction.amount));
    const isIncoming = parseFloat(transaction.amount) > 0;

    const entries = [];

    if (isIncoming) {
      entries.push({
        accountId: '1930',
        accountName: '1930 - Företagskonto/checkräkningskonto',
        debit: amount,
        credit: 0
      });
      entries.push({
        accountId: '',
        accountName: '',
        debit: 0,
        credit: amount
      });
    } else {
      entries.push({
        accountId: '1930',
        accountName: '1930 - Företagskonto/checkräkningskonto',
        debit: 0,
        credit: amount
      });
      entries.push({
        accountId: '',
        accountName: '',
        debit: amount,
        credit: 0
      });
    }

    setAccountEntries(entries);
    setCurrentStep(2);
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (selectedSuggestion) {
        setCurrentStep(2);
      } else {
        setError('Välj ett förslag eller klicka "Hoppa över"');
      }
    } else if (currentStep === 2) {
      // Validate entries
      if (!validateEntries()) {
        setError('Kontrollera att alla konton är ifyllda och att debet = kredit');
        return;
      }
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setError(null);
  };

  const validateEntries = () => {
    if (accountEntries.length === 0) return false;

    // Check all accounts are filled
    for (const entry of accountEntries) {
      if (!entry.accountId) return false;
    }

    // Check debit = credit
    const totalDebit = accountEntries.reduce((sum, entry) => sum + (parseFloat(entry.debit) || 0), 0);
    const totalCredit = accountEntries.reduce((sum, entry) => sum + (parseFloat(entry.credit) || 0), 0);

    return Math.abs(totalDebit - totalCredit) < 0.01; // Allow small floating point differences
  };

  const handleCreateVerification = async () => {
    if (!selectedFiscalYear) {
      setError('Välj räkenskapsår');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const basePath = transactionType === 'skatteverket'
        ? `${companyId}/accounting/reconciliation/skatteverket/statements/${statementId}/verification`
        : `${companyId}/accounting/reconciliation/banking/statements/${statementId}/verification`;

      const apiUrl = getApiUrl(basePath);

      const accountMappings = {
        debitAccount: accountEntries[0].accountId,
        creditAccount: accountEntries[1].accountId
      };

      // For bank transactions, also include legacy bankAccount/counterAccount fields
      if (transactionType === 'bank') {
        accountMappings.bankAccount = accountEntries[0].accountId;
        accountMappings.counterAccount = accountEntries[1].accountId;
      }

      const requestBody = {
        fiscalYearId: selectedFiscalYear,
        accountMappings,
        description,
        notes,
        series,
        autoLink: true
      };

      const response = await axios.post(apiUrl, requestBody, { withCredentials: true });

      if (response.data.success) {
        if (onSuccess) {
          onSuccess(response.data);
        }
        onHide();
      }
    } catch (err) {
      console.error('Error creating verification:', err);
      setError(err.response?.data?.message || 'Kunde inte skapa verifikat');
    } finally {
      setCreating(false);
    }
  };

  const updateAccountEntry = (index, field, value) => {
    const newEntries = [...accountEntries];
    newEntries[index] = {
      ...newEntries[index],
      [field]: value
    };
    setAccountEntries(newEntries);
  };

  const getConfidenceBadgeColor = (confidence) => {
    if (confidence >= 0.75) return 'success';
    if (confidence >= 0.5) return 'info';
    return 'warning';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(amount);
  };

  if (!transaction) return null;

  const isIncoming = parseFloat(transaction.amount) > 0;
  const totalDebit = accountEntries.reduce((sum, entry) => sum + (parseFloat(entry.debit) || 0), 0);
  const totalCredit = accountEntries.reduce((sum, entry) => sum + (parseFloat(entry.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: '#eff6ff',
              color: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FileText size={20} />
          </div>
          <div>
            <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>
              Skapa Verifikat
            </span>
            <div style={{ fontSize: '0.875rem', fontWeight: '400', color: '#6b7280' }}>
              Steg {currentStep} av 3
            </div>
          </div>
        </Modal.Title>
        <button
          type="button"
          className="btn-close"
          onClick={onHide}
          aria-label="Close"
        />
      </Modal.Header>

      <Modal.Body className="px-4 py-4">
        {/* Progress Bar */}
        <ProgressBar className="mb-4" style={{ height: '6px' }}>
          <ProgressBar variant="primary" now={(currentStep / 3) * 100} />
        </ProgressBar>

        {/* Transaction Info Summary */}
        <Card className="mb-4" style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                  Transaktion
                </div>
                <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1f2937' }}>
                  {transaction.description}
                </div>
              </div>
              <div className="text-end">
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                  Belopp
                </div>
                <div
                  style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: isIncoming ? '#16a34a' : '#dc2626'
                  }}
                >
                  {isIncoming ? <TrendingUp size={16} className="me-1" /> : <TrendingDown size={16} className="me-1" />}
                  {formatCurrency(transaction.amount)}
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <AlertCircle size={16} className="me-2" />
            {error}
          </Alert>
        )}

        {/* Step 1: Suggestion Selection */}
        {currentStep === 1 && (
          <div>
            <div className="mb-3">
              <h6 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                <Lightbulb size={18} className="me-2" style={{ color: '#f59e0b' }} />
                Föreslagna Konton
              </h6>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                Välj ett förslag baserat på AI-analys eller hoppa över för att skapa manuellt.
              </p>
            </div>

            {loading && (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <div className="mt-2" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Analyserar transaktion...
                </div>
              </div>
            )}

            {!loading && suggestions.length > 0 && (
              <div>
                {suggestions.map((suggestion, index) => (
                  <Card
                    key={index}
                    className="mb-2"
                    style={{
                      border: selectedSuggestion === suggestion ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => handleSuggestionSelect(suggestion)}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <strong style={{ fontSize: '0.875rem', color: '#1f2937' }}>
                              Konto {suggestion.counterAccount}
                            </strong>
                            <Badge bg={getConfidenceBadgeColor(suggestion.confidence)}>
                              {(suggestion.confidence * 100).toFixed(0)}% säker
                            </Badge>
                            {suggestion.source && (
                              <Badge bg="secondary" style={{ fontSize: '0.625rem' }}>
                                {suggestion.source === 'historical' ? 'Historisk' :
                                 suggestion.source === 'keyword' ? 'Nyckelord' : 'Belopp'}
                              </Badge>
                            )}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {suggestion.reason}
                          </div>
                        </div>
                        <div>
                          {selectedSuggestion === suggestion && (
                            <CheckCircle size={20} style={{ color: '#3b82f6' }} />
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}

            {!loading && suggestions.length === 0 && (
              <Alert variant="info">
                <AlertCircle size={16} className="me-2" />
                Inga automatiska förslag hittades. Klicka "Hoppa över" för att skapa manuellt.
              </Alert>
            )}
          </div>
        )}

        {/* Step 2: Edit Entries */}
        {currentStep === 2 && (
          <div>
            <div className="mb-3">
              <h6 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                <Edit3 size={18} className="me-2" style={{ color: '#3b82f6' }} />
                Redigera Verifikationsrader
              </h6>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                Granska och redigera kontona. Kontrollera att debet = kredit.
              </p>
            </div>

            <Form>
              {/* Fiscal Year */}
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                  Räkenskapsår
                </Form.Label>
                <Form.Select
                  value={selectedFiscalYear || ''}
                  onChange={(e) => setSelectedFiscalYear(e.target.value)}
                  style={{ fontSize: '0.875rem' }}
                >
                  {fiscalYears.map((fy) => (
                    <option key={fy.fiscal_year_id} value={fy.fiscal_year_id}>
                      {fy.name} ({fy.start_date} - {fy.end_date})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Description */}
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                  Beskrivning
                </Form.Label>
                <Form.Control
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{ fontSize: '0.875rem' }}
                />
              </Form.Group>

              {/* Account Entries */}
              <div className="mb-3">
                <Form.Label style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                  Kontorader
                </Form.Label>

                {accountEntries.map((entry, index) => (
                  <Row key={index} className="mb-2">
                    <Col md={6}>
                      <Form.Control
                        type="text"
                        placeholder="Konto (t.ex. 1930)"
                        value={entry.accountId}
                        onChange={(e) => updateAccountEntry(index, 'accountId', e.target.value)}
                        style={{ fontSize: '0.875rem' }}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Control
                        type="number"
                        placeholder="Debet"
                        value={entry.debit}
                        onChange={(e) => updateAccountEntry(index, 'debit', parseFloat(e.target.value) || 0)}
                        style={{ fontSize: '0.875rem' }}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Control
                        type="number"
                        placeholder="Kredit"
                        value={entry.credit}
                        onChange={(e) => updateAccountEntry(index, 'credit', parseFloat(e.target.value) || 0)}
                        style={{ fontSize: '0.875rem' }}
                      />
                    </Col>
                  </Row>
                ))}

                {/* Balance Check */}
                <Card style={{ backgroundColor: isBalanced ? '#f0fdf4' : '#fef2f2', border: 'none' }} className="mt-2">
                  <Card.Body className="p-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                        {isBalanced ? (
                          <><CheckCircle size={16} className="me-2" style={{ color: '#16a34a' }} />Balanserad</>
                        ) : (
                          <><AlertCircle size={16} className="me-2" style={{ color: '#dc2626' }} />Obalanserad</>
                        )}
                      </span>
                      <div style={{ fontSize: '0.875rem' }}>
                        <span className="me-3">Debet: {formatCurrency(totalDebit)}</span>
                        <span>Kredit: {formatCurrency(totalCredit)}</span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>

              {/* Series */}
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                      Serie
                    </Form.Label>
                    <Form.Select
                      value={series}
                      onChange={(e) => setSeries(e.target.value)}
                      style={{ fontSize: '0.875rem' }}
                    >
                      <option value="A">A - Affärshändelser</option>
                      <option value="K">K - Kassa</option>
                      <option value="B">B - Bank</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </div>
        )}

        {/* Step 3: Review */}
        {currentStep === 3 && (
          <div>
            <div className="mb-3">
              <h6 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                <Eye size={18} className="me-2" style={{ color: '#16a34a' }} />
                Granska Verifikat
              </h6>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                Kontrollera att allt är korrekt innan du skapar verifikatet.
              </p>
            </div>

            <Card>
              <Card.Body>
                <div className="mb-3">
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                    Beskrivning
                  </div>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1f2937' }}>
                    {description}
                  </div>
                </div>

                <div className="mb-3">
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '8px' }}>
                    Kontorader
                  </div>
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th style={{ fontSize: '0.75rem' }}>Konto</th>
                        <th style={{ fontSize: '0.75rem' }} className="text-end">Debet</th>
                        <th style={{ fontSize: '0.75rem' }} className="text-end">Kredit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accountEntries.map((entry, index) => (
                        <tr key={index}>
                          <td style={{ fontSize: '0.875rem' }}>{entry.accountId}</td>
                          <td style={{ fontSize: '0.875rem' }} className="text-end">
                            {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                          </td>
                          <td style={{ fontSize: '0.875rem' }} className="text-end">
                            {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: '2px solid #1f2937', fontWeight: '600' }}>
                        <td>Summa</td>
                        <td className="text-end">{formatCurrency(totalDebit)}</td>
                        <td className="text-end">{formatCurrency(totalCredit)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                    Serie
                  </div>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1f2937' }}>
                    {series}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button
          variant="outline-secondary"
          onClick={currentStep === 1 ? onHide : handleBack}
          disabled={creating}
        >
          <ChevronLeft size={16} className="me-1" />
          {currentStep === 1 ? 'Avbryt' : 'Tillbaka'}
        </Button>

        {currentStep === 1 && (
          <Button variant="outline-primary" onClick={handleSkipSuggestions}>
            Hoppa över
          </Button>
        )}

        {currentStep < 3 && (
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={currentStep === 1 && !selectedSuggestion && suggestions.length > 0}
          >
            Nästa
            <ChevronRight size={16} className="ms-1" />
          </Button>
        )}

        {currentStep === 3 && (
          <Button
            variant="success"
            onClick={handleCreateVerification}
            disabled={creating || !isBalanced}
          >
            {creating ? (
              <>
                <Spinner size="sm" className="me-2" />
                Skapar...
              </>
            ) : (
              <>
                <CheckCircle size={16} className="me-2" />
                Skapa Verifikat
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CreateVerificationFromTransactionModal;
